import React from 'react';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import type { MeliPublicationDraftResponse } from 'src/api/generated';
import type { TranslateParams } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

interface PropertyMeliDraftValidationCardProps {
  draft: MeliPublicationDraftResponse | null;
  draftIssueVariant: 'success' | 'warning' | 'danger' | 'secondary' | null;
  getDraftIssueFieldLabel: (field: string) => string;
  getDraftIssueMessage: (
    issue: MeliPublicationDraftResponse['issues'][number]
  ) => string;
  groupedDraftIssues: {
    sectionKey: string;
    issues: MeliPublicationDraftResponse['issues'];
  }[];
  preparingDraft: boolean;
  selectedListingTypeName: string;
  t: (key: TranslationKey, params?: TranslateParams) => string;
}

/**
 * Compact validation summary for the current Mercado Libre draft.
 */
export const PropertyMeliDraftValidationCard: React.FC<
  PropertyMeliDraftValidationCardProps
> = ({
  draft,
  draftIssueVariant,
  getDraftIssueFieldLabel,
  getDraftIssueMessage,
  groupedDraftIssues,
  preparingDraft,
  selectedListingTypeName,
  t,
}) => {
  return (
    <div className="rounded-lg border border-neutral-200 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Text size="small" weight="medium">
          {t('catalog.properties.meli.draft.title')}
        </Text>
        {draft ? (
          <StatusPill
            label={t('catalog.properties.meli.draft.issuesCount', {
              count: draft.issues.length,
            })}
            variant={draftIssueVariant ?? 'success'}
          />
        ) : null}
      </div>

      {preparingDraft && !draft ? (
        <div className="mt-3 space-y-2">
          <TextSkeleton className="h-4 w-32" />
          <TextSkeleton className="h-4 w-full" />
        </div>
      ) : draft ? (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Text size="tiny" className="text-slate-500">
              {t('catalog.properties.meli.form.listingType')}
            </Text>
            <StatusPill
              label={t('catalog.properties.meli.draft.selectedListingType', {
                listingTypeName: selectedListingTypeName,
              })}
              variant="secondary"
            />
          </div>

          {draft.issues.length > 0 ? (
            <div className="space-y-3">
              {groupedDraftIssues.map((group) => (
                <div
                  key={group.sectionKey}
                  className="rounded-md border border-warning-200 bg-warning-50 px-3 py-3"
                >
                  <Text size="small" weight="medium" className="text-slate-900">
                    {t(group.sectionKey as TranslationKey)}
                  </Text>
                  <ul className="mt-2 space-y-2">
                    {group.issues.map((issue) => (
                      <li key={`${issue.field}:${issue.code}`}>
                        <Text size="small" weight="medium">
                          {getDraftIssueFieldLabel(issue.field)}
                        </Text>
                        <Text size="small" color="secondary">
                          {getDraftIssueMessage(issue)}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <Text size="small" color="secondary">
              {t('catalog.properties.meli.draft.noIssues')}
            </Text>
          )}
        </div>
      ) : (
        <Text size="small" color="secondary" className="mt-3">
          {t('catalog.properties.meli.draft.empty')}
        </Text>
      )}
    </div>
  );
};
